import {usePlantConfig, useStoppageCreate, stoppageDefaultObject} from '@domain/shifthandover'
import {PermissionType} from '@hconnect/apiclient'
import {StoppageCreate as StoppageCreateDto} from '@hconnect/common/stoppages/types/stoppage.types'
import {StoppageDto as Stoppage, AttachmentFile, DisabledFields} from '@hconnect/common/types'
import {LoadingSwitcher} from '@hconnect/uikit/src/lib2'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {FeatureFlagKey, useCommonFeatureFlag} from '@hproduce/feature-flags'
import {checkPermission, usePermissionsParsedQuery} from '@hproduce/permissions'
import {LinearProgress} from '@mui/material'
import {StoppageFormContainer, EventAttachmentUpload} from '@widgets/common-shift-event'
import {AxiosError} from 'axios'
import React, {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'

const createNew = (initialData: Partial<Stoppage> = {}): Partial<Stoppage> => {
  return stoppageDefaultObject<Partial<Stoppage>>(initialData)
}

type StoppageCreateProps = {
  plantId: string
  'data-test-id'?: string
  initialData?: Partial<Stoppage>
  doClose(): void
  onSuccessSubmit?(stoppage: Stoppage): void
  onError?(error: AxiosError): void
  renderFormHeaderActions?: (props: {doClose: () => void}) => React.ReactNode
  renderFormFooterActions?: (props: {
    doSubmit: () => void
    doClose: () => void
    isDirty: boolean
    isSubmitDisabled: boolean
  }) => React.ReactNode
  bodyWithPadding?: boolean
  withSalesImpact?: boolean
  withPriority?: boolean
  withCategory?: boolean
  eventTypeLabelPrefix?: string
  skipOverlapValidation?: boolean
}

export const StoppageCreate: React.FC<StoppageCreateProps> = ({
  doClose,
  plantId,
  onSuccessSubmit,
  onError,
  initialData,
  renderFormHeaderActions,
  renderFormFooterActions,
  bodyWithPadding = true,
  eventTypeLabelPrefix,
  withPriority,
  withCategory,
  skipOverlapValidation,
  ...props
}) => {
  const {
    t,
    i18n: {language}
  } = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const [files, setFiles] = useState<Array<AttachmentFile>>([])
  const {data: config, isLoading: isConfigLoading} = usePlantConfig({plantId, language})
  const {data: permissions} = usePermissionsParsedQuery()
  const isMovieAttachmentEnabled = useCommonFeatureFlag(FeatureFlagKey.MovieAttachment)
  const isAutocompleteProcessStage = useCommonFeatureFlag(FeatureFlagKey.AutocompleteProcessStage, {
    plantId
  })

  const plantContext = {plantId, countryId: config?.country}

  const hasPermissionChecker = useCallback(
    (permissionType: PermissionType) =>
      permissions && plantContext.countryId
        ? checkPermission(permissionType, plantContext.plantId, permissions, [
            plantContext.countryId
          ])
        : false,
    [plantContext.plantId, plantContext.countryId, permissions]
  )

  const createAction = useStoppageCreate(plantId, {
    onSuccess: (stoppage) => {
      onSuccessSubmit?.(stoppage)
      spawnSuccessToast(t('stoppage.createMessage.success'), {
        closeOnClick: true
      })
    },
    onError: (error) => {
      onError?.(error)
      const errorMessage =
        error.response?.status === 409
          ? t('stoppage.createMessage.errorConflict')
          : t('stoppage.createMessage.error')
      spawnErrorToast(errorMessage, {closeOnClick: true})
    }
  })

  const item: Partial<Stoppage> = useMemo(() => createNew(initialData), [initialData])

  const doSubmit = React.useCallback(
    (item: StoppageCreateDto) => {
      createAction.mutate({
        ...item,
        attachments: files
      })
    },
    [createAction, files]
  )

  const disableStartEndChange = !!(initialData?.stoppageStart || initialData?.stoppageEnd)
  const isSplitStoppage = !!initialData?.originalSplitStoppageId
  const disabledFields: DisabledFields = {
    stoppageStart: disableStartEndChange,
    stoppageEnd: disableStartEndChange,
    upmDepartmentId: isSplitStoppage,
    stoppageType: isSplitStoppage
  }

  if (isConfigLoading) {
    return <LinearProgress data-test-id={`${props['data-test-id']}-loader`} />
  }

  // guard to satisfy non null types of the stoppage form container
  if (!config) {
    return null
  }

  return (
    <LoadingSwitcher isLoading={createAction.isPending}>
      <StoppageFormContainer
        stoppageCodes={{
          kilnStoppageCodes: config.kilnStoppageCodes,
          rawMillStoppageCodes: config.rawMillStoppageCodes,
          finishMillStoppageCodes: config.finishMillStoppageCodes,
          alternativeFuelInstallationStoppageCodes: config.alternativeFuelInstallationStoppageCodes
        }}
        timezone={config.timezone}
        data-test-id={props['data-test-id']}
        isCreateMode={true}
        item={item}
        doClose={doClose}
        doSubmit={doSubmit}
        plantId={plantId}
        isAutocompleteProcessStage={!!isAutocompleteProcessStage}
        renderFormHeaderActions={renderFormHeaderActions}
        renderFormFooterActions={renderFormFooterActions}
        bodyWithPadding={bodyWithPadding}
        hiddenFields={{
          hasSalesImpact: !props.withSalesImpact,
          stoppageType: true,
          priority: !withPriority,
          category: !withCategory
        }}
        eventTypeLabelPrefix={eventTypeLabelPrefix}
        disabledFields={disabledFields}
        isEquipmentAvailable={config.isEquipmentAvailable}
        skipOverlapValidation={skipOverlapValidation}
      >
        <EventAttachmentUpload
          setAttachments={setFiles}
          attachments={files}
          isMovieAttachmentEnabled={isMovieAttachmentEnabled}
          hasPermission={hasPermissionChecker}
        />
      </StoppageFormContainer>
    </LoadingSwitcher>
  )
}
